import { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import {
  FreshStrawEmbryoPatch,
  usePatchFreshStrawEmbryoMutation,
} from '@/app/services/freshStrawRest';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface FreshStrawEmbryoEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  embryoId: string | null;
  initial?: Partial<
    Record<
      | 'mating'
      | 'clientRecipient'
      | 'cane'
      | 'comment'
      | 'strawNumber'
      | 'embTagNumber'
      | 'stage'
      | 'grade'
      | 'quality'
      | 'distance'
      | 'rightLeft',
      string
    >
  >;
  onSaved?: () => void;
}

type Draft = {
  mating: string;
  clientRecipient: string;
  cane: string;
  comment: string;
  strawNumber: string;
  embTagNumber: string;
  stage: string;
  grade: string;
  quality: string;
  distance: string;
  rightLeft: string;
};

const EMPTY: Draft = {
  mating: '',
  clientRecipient: '',
  cane: '',
  comment: '',
  strawNumber: '',
  embTagNumber: '',
  stage: '',
  grade: '',
  quality: '',
  distance: '',
  rightLeft: '',
};

function fromInitial(
  initial?: FreshStrawEmbryoEditDialogProps['initial'],
): Draft {
  return { ...EMPTY, ...(initial ?? {}) };
}

export function FreshStrawEmbryoEditDialog({
  open,
  onOpenChange,
  embryoId,
  initial,
  onSaved,
}: FreshStrawEmbryoEditDialogProps) {
  const dispatch = useDispatch();
  const [draft, setDraft] = useState<Draft>(fromInitial(initial));
  const [patch, patchState] = usePatchFreshStrawEmbryoMutation();

  useEffect(() => {
    if (open) setDraft(fromInitial(initial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, embryoId]);

  if (!embryoId) return null;

  const handleSave = async () => {
    const body: FreshStrawEmbryoPatch = {
      mating: draft.mating,
      clientRecipient: draft.clientRecipient,
      cane: draft.cane,
      comment: draft.comment,
      strawNumber: toIntOrNull(draft.strawNumber),
      embTagNumber: toIntOrNull(draft.embTagNumber),
      stage: toIntOrNull(draft.stage),
      grade: toIntOrNull(draft.grade),
      quality: toIntOrNull(draft.quality),
      distance: toIntOrNull(draft.distance),
      rightLeft: toIntOrNull(draft.rightLeft),
    };
    try {
      await patch({ embryoId, ...body }).unwrap();
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Embryo updated',
          life: ToastLife.short,
        }),
      );
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Could not save embryo',
          detail:
            err && typeof err === 'object' && 'data' in err
              ? ((err as { data?: { message?: string } }).data?.message ??
                'Save failed.')
              : 'Save failed.',
          life: ToastLife.long,
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit embryo</DialogTitle>
        </DialogHeader>
        <DialogBody className="grid grid-cols-2 gap-3">
          <Field label="Mating">
            <input
              type="text"
              value={draft.mating}
              onChange={(e) =>
                setDraft((d) => ({ ...d, mating: e.target.value }))
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Recip">
            <input
              type="text"
              value={draft.clientRecipient}
              onChange={(e) =>
                setDraft((d) => ({ ...d, clientRecipient: e.target.value }))
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Cane">
            <input
              type="text"
              value={draft.cane}
              onChange={(e) =>
                setDraft((d) => ({ ...d, cane: e.target.value }))
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Straw #">
            <input
              type="number"
              value={draft.strawNumber}
              onChange={(e) =>
                setDraft((d) => ({ ...d, strawNumber: e.target.value }))
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Ear Tag">
            <input
              type="number"
              value={draft.embTagNumber}
              onChange={(e) =>
                setDraft((d) => ({ ...d, embTagNumber: e.target.value }))
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Stage">
            <input
              type="number"
              value={draft.stage}
              onChange={(e) =>
                setDraft((d) => ({ ...d, stage: e.target.value }))
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Grade">
            <input
              type="number"
              value={draft.grade}
              onChange={(e) =>
                setDraft((d) => ({ ...d, grade: e.target.value }))
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Quality">
            <input
              type="number"
              value={draft.quality}
              onChange={(e) =>
                setDraft((d) => ({ ...d, quality: e.target.value }))
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Distance">
            <input
              type="number"
              value={draft.distance}
              onChange={(e) =>
                setDraft((d) => ({ ...d, distance: e.target.value }))
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field label="CL L/R">
            <input
              type="number"
              value={draft.rightLeft}
              onChange={(e) =>
                setDraft((d) => ({ ...d, rightLeft: e.target.value }))
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </Field>
          <Field label="Comment" className="col-span-2">
            <textarea
              value={draft.comment}
              rows={2}
              onChange={(e) =>
                setDraft((d) => ({ ...d, comment: e.target.value }))
              }
              className="rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={patchState.isLoading}>
            {patchState.isLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`flex flex-col gap-1 ${className ?? ''}`}>
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

function toIntOrNull(s: string): number | null {
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
